// =============================================================================
// Calendario laboral y festivos (backend/app/schemas/calendar.py)
// =============================================================================
import { apiClient } from './http';

/** Espejo de WorkCalendarSummary — lo que devuelve GET /reports/calendars. */
export interface WorkCalendarSummary {
  id: number;
  name: string;
  year: number;
  region: string | null;
  is_active: boolean;
  holiday_count: number;
}

/** Espejo de HolidayResponse (backend/app/schemas/calendar.py). */
export interface HolidayView {
  id: number;
  calendar_id: number;
  date: string;          // "YYYY-MM-DD"
  name: string;
  holiday_type: string;  // NATIONAL | REGIONAL | LOCAL | COMPANY
  is_premium_day: boolean;
  premium_factor: number;
}

/** Espejo de WorkCalendarResponse — lo que devuelven POST/PATCH /calendars(/{id}). */
export interface WorkCalendarView {
  id: number;
  tenant_id: number;
  name: string;
  year: number;
  region: string | null;
  is_active: boolean;
  holidays: HolidayView[];
}

/** GET /reports/calendars(?year=). Nota: NO trae `holidays`, trae `holiday_count`. */
export async function getCalendars(year?: number): Promise<WorkCalendarSummary[]> {
  const params = year ? `?year=${year}` : '';
  const res = await apiClient.get<WorkCalendarSummary[]>(`/reports/calendars${params}`);
  return res.data;
}

/**
 * Festivo tal como lo devuelve la LECTURA de reports.py: ese endpoint arma el
 * dict a mano y NO incluye `calendar_id` (a diferencia de HolidayResponse en las
 * escrituras). Se tipa aparte para no prometer un campo que no llega. Para editar
 * o borrar, usa el id del calendario seleccionado, no `holiday.calendar_id`.
 */
export type CalendarHolidayItem = Omit<HolidayView, 'calendar_id'>;

/**
 * GET /reports/calendars/{id}/holidays. El backend envuelve la lista en
 * `{ calendar: {...}, holidays: [...] }`; aquí se desenvuelve para devolver
 * solo los festivos.
 */
export async function getCalendarHolidays(id: number): Promise<CalendarHolidayItem[]> {
  const res = await apiClient.get<{
    calendar: { id: number; name: string; year: number };
    holidays: CalendarHolidayItem[];
  }>(`/reports/calendars/${id}/holidays`);
  return res.data.holidays;
}

export async function createCalendar(data: {
  name: string;
  year: number;
  region?: string | null;
  is_active?: boolean;
}): Promise<WorkCalendarView> {
  const res = await apiClient.post<WorkCalendarView>('/calendars', data);
  return res.data;
}

/** Siembra festivos nacionales/autonómicos. Idempotente: salta fechas ya presentes. */
export async function seedCalendar(id: number): Promise<WorkCalendarView> {
  const res = await apiClient.post<WorkCalendarView>(`/calendars/${id}/seed`);
  return res.data;
}

export async function addHoliday(
  id: number,
  data: { date: string; name: string; holiday_type?: string }
): Promise<HolidayView> {
  const res = await apiClient.post<HolidayView>(`/calendars/${id}/holidays`, data);
  return res.data;
}

/** Editar un festivo (p. ej. mover la fecha porque el convenio la cambia). */
export async function updateHoliday(
  id: number,
  holidayId: number,
  data: { date?: string; name?: string }
): Promise<HolidayView> {
  const res = await apiClient.patch<HolidayView>(`/calendars/${id}/holidays/${holidayId}`, data);
  return res.data;
}

export async function deleteHoliday(id: number, holidayId: number): Promise<void> {
  await apiClient.delete(`/calendars/${id}/holidays/${holidayId}`);
}

export async function setCalendarActive(id: number, is_active: boolean): Promise<WorkCalendarView> {
  const res = await apiClient.patch<WorkCalendarView>(`/calendars/${id}`, { is_active });
  return res.data;
}

export async function deleteCalendar(id: number): Promise<void> {
  await apiClient.delete(`/calendars/${id}`);
}
